'use client';

import { c as _c } from "react/compiler-runtime";
import { Liveline } from '@morfi/chart';
import { useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import type { CSSProperties, NamedExoticComponent } from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { cn } from '@/lib/ui/utils';

/** Single point of the total-PnL line, oldest → newest. `time` is unix seconds. */
export interface PnlLivelinePoint {
  readonly time: number;
  readonly value: number;
}
export interface PnlCardLivelineProps {
  /**
   * Total-PnL series, oldest → newest. The card only mounts this wrapper once
   * `adaptTotalPnlLine` has produced a non-null series (length ≥ 2), so the
   * loading / empty branches stay upstream.
   */
  readonly series: ReadonlyArray<PnlLivelinePoint>;
  /** Drives the line / fill / endpoint accent. Follows the sign of the 24h delta. */
  readonly tone: 'success' | 'destructive';
  readonly className?: string;
}
type PnlLivelineComponent = NamedExoticComponent<PnlCardLivelineProps>;

// LiveChart's default endpoint pulse expands to ~21 px. Pad the canvas on the
// three sides where the pulse can reach (top/right/bottom) so the ring is not
// clipped at the card-well edge. Left padding stays minimal — the line starts
// there and has no endpoint. Mirrors `outcome-liveline.tsx`.
const ENDPOINT_PAD: number = Math.ceil(21);
const MIN_POINTS = 2;

// Local mask override for the dotted underlay. The shared `chart-surface-underlay`
// utility uses a 12% / 88% vertical fade — too narrow to absorb a 10px dot tile
// in the 80px chart well, leaving the bottom row hard-cropped. Widen both fade
// zones (top 0-30%, bottom 65-100%) and preserve the horizontal fade. Mirrors
// `outcome-sparkline.tsx`'s hero treatment; both wells are 80px tall.
const UNDERLAY_MASK_VALUE = 'linear-gradient(to bottom, transparent 0%, black 30%, black 65%, transparent 100%), linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)';
const UNDERLAY_MASK: CSSProperties = {
  maskImage: UNDERLAY_MASK_VALUE,
  WebkitMaskImage: UNDERLAY_MASK_VALUE
};

// LiveChart paints into a canvas, which cannot resolve `var(--…)`. Resolve the
// explicit line colour here so it remains the chart's highest-priority colour.
function resolveCssVar(input: string, fallback: string = input): string {
  if (typeof document === 'undefined') return fallback;
  const match = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(input);
  if (!match) return input;
  const varName = match[1] ?? '';
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (resolved.length > 0) return resolved;
  const inlineFallback = match[2];
  return inlineFallback !== undefined ? inlineFallback.trim() : fallback;
}

/**
 * Portfolio total-PnL preview — canvas-based `<LiveChart>` that replaces the
 * static SVG sparkline in the PnL card's populated branch. Wraps the chart
 * with all instrument chrome off (no axes, no labels, no crosshair, no badge)
 * and `momentum` colouring off (the endpoint dot keeps the delta-sign accent
 * rather than flipping green/red), matching the decorative configuration used
 * by the featured-market hero (`outcome-liveline.tsx`).
 *
 * The series is a historical snapshot keyed to the fetched PnL line, so the
 * `nowOverride` anchor is the last point's timestamp (no live tick) and the
 * fixed `window` spans first → last so the line fills the well edge-to-edge.
 *
 * Off-screen rendering is gated by an `IntersectionObserver` (200 px
 * `rootMargin` pre-fetch) with a sticky mount latch, so the canvas RAF loop
 * never runs while the card is scrolled away.
 */
function PnlCardLivelineView(t0) {
  const $ = _c(26);
  const {
    series,
    tone,
    className
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const theme = resolvedTheme === "light" ? "light" : "dark";
  const reduceMotion = useReducedMotion() === true;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: "200px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(containerRef, t1);
  const [mounted, setMounted] = useState(false);
  let t2;
  let t3;
  if ($[1] !== isIntersecting || $[2] !== mounted) {
    t2 = () => {
      if (isIntersecting && !mounted) {
        setMounted(true);
      }
    };
    t3 = [isIntersecting, mounted];
    $[1] = isIntersecting;
    $[2] = mounted;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const t4 = tone === "destructive" ? "var(--data-loss)" : "var(--data-profit)";
  let t5;
  if ($[5] !== t4) {
    t5 = resolveCssVar(t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const color = t5;
  let t6;
  if ($[7] !== series) {
    t6 = series.map(_temp);
    $[7] = series;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const points = t6;
  const firstTime = points[0]?.time ?? 0;
  const lastTime = points[points.length - 1]?.time ?? firstTime;
  const lastValue = points[points.length - 1]?.value ?? 0;
  const windowSecs = Math.max(0, lastTime - firstTime);
  let t7;
  if ($[9] !== reduceMotion) {
    t7 = reduceMotion ? {
      lerpSpeed: 1
    } : {};
    $[9] = reduceMotion;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const smoothingProp = t7;
  let t8;
  if ($[11] !== className) {
    t8 = cn("pointer-events-none relative h-full w-full", "[--chart-surface-dot-pitch:10px] [--chart-surface-dot-inner:1px] [--chart-surface-dot-outer:1.75px]", "dark:[--pattern-dot-grid-color:var(--gray-700)]", className);
    $[11] = className;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div aria-hidden="true" className="chart-surface-underlay opacity-60" style={UNDERLAY_MASK}><div className="chart-surface-vignette absolute inset-0" /><div className="chart-surface-dots morfi-chart-dots-drift absolute inset-0" /></div>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== color || $[15] !== lastValue || $[16] !== mounted || $[17] !== points || $[18] !== smoothingProp || $[19] !== theme || $[20] !== windowSecs) {
    t10 = mounted && points.length >= MIN_POINTS ? <Liveline data={points} value={lastValue} color={color} lineWidth={1.5} theme={theme} window={windowSecs} grid={false} formatTime={_temp2} scrub={false} pulse={true} badge={false} momentum={false} fill={true} {...smoothingProp} padding={{
      top: ENDPOINT_PAD,
      right: ENDPOINT_PAD,
      bottom: ENDPOINT_PAD,
      left: 4
    }} className="block h-full w-full" /> : null;
    $[14] = color;
    $[15] = lastValue;
    $[16] = mounted;
    $[17] = points;
    $[18] = smoothingProp;
    $[19] = theme;
    $[20] = windowSecs;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8 || $[24] !== tone) {
    t11 = <div ref={containerRef} className={t8} aria-hidden={true} role="presentation" data-testid="pnl-card-liveline" data-tone={tone}>{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = tone;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function _temp2() {
  return "";
}
function _temp(p) {
  return {
    time: p.time,
    value: p.value
  };
}
function sameProps(previous: PnlCardLivelineProps, next: PnlCardLivelineProps): boolean {
  if (previous.tone !== next.tone || previous.className !== next.className || previous.series.length !== next.series.length) {
    return false;
  }
  // ALLOW: React Doctor cannot follow the preceding early-return length guard.
  // REASON: The arrays are already proven equal-length before the deep point comparison.
  // react-doctor-disable-next-line react-doctor/js-length-check-first -- length equality is checked above.
  return previous.series.every((point, index) => point.time === next.series[index]?.time && point.value === next.series[index]?.value);
}

// ALLOW: The canvas boundary requires value-based isolation across rebuilt series arrays.
// REASON: React Compiler uses identity dependencies and cannot replace this deep point comparator.
// react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization -- intentional canvas isolation.
const Isolated: PnlLivelineComponent = memo(PnlCardLivelineView, sameProps); // ARBITRARY: canvas isolation

export const PnlCardLiveline: PnlLivelineComponent = Isolated;